import { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import AppContext from '../../context';
import Data from '../../repository';
import Loading from '../components/Loading';
import SumupWidget from '../components/SumupWidget';
import { hasMarketingConsent, trackMetaEvent, storePendingPurchase, retrievePendingPurchase } from '../../utils/metaPixel';

import styles from '../../styles/Tokens.module.scss';

const formatMoney = (amount) => `£${(Number(amount || 0) / 100).toFixed(2)}`;
const formatTokens = (amount) => `${Number(amount || 0)} token${Number(amount || 0) === 1 ? '' : 's'}`;

function WalletCheckout({ basePath = '/dashboard/wallet' }) {
	const { id } = useParams();
	const navigate = useNavigate();
	const context = useContext(AppContext);
	const isHybrid = context.domain?.businessType === 'hybrid' || context.domain?.businessType === 'hybrid-new';
	const formatBalance = (amount) => isHybrid ? formatTokens(amount) : formatMoney(amount);
	const showMarketingConsent = !!context.domain?.metaPixelEnabled;

	const [loading, setLoading] = useState(true);
	const [walletPackage, setWalletPackage] = useState(null);
	const [error, setError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [sumupCheckoutId, setSumupCheckoutId] = useState(null);
	const [sumupTransactionId, setSumupTransactionId] = useState(null);
	const [marketingConsent, setMarketingConsent] = useState(() => hasMarketingConsent());
	const sumupPollRef = useRef(null);

	const isSumup = context.domain?.paymentProcessor === 'SumUp';

	const updateMarketingConsent = (checked) => {
		setMarketingConsent(checked);
		localStorage.setItem('marketingConsent', checked ? 'true' : 'false');
	};

	useEffect(() => {
		const init = async () => {
			try {
				const result = await Data.fetchPublicWalletPackages();
				const selected = (result.data || []).find((item) => item._id === id);
				if (!selected) throw new Error('Wallet package not found');
				setWalletPackage(selected);
			} catch (error) {
				navigate(basePath);
			} finally {
				setLoading(false);
			}
		};

		init();
	}, [id, navigate, basePath]);

	const trackWalletInitiateCheckout = () => {
		const pixelId = context.domain?.metaPixelEnabled ? context.domain?.metaPixelId : null;
		if (!pixelId || !walletPackage) return;
		const currency = context.domain?.metaCurrency || 'GBP';
		const value = walletPackage.purchaseAmount / 100;
		trackMetaEvent({ pixelId, eventName: 'InitiateCheckout', params: { content_type: 'wallet', content_ids: [walletPackage._id], content_name: walletPackage.name, value, currency } });
		storePendingPurchase('wallet', { value, name: walletPackage.name, currency });
	};

	const fireWalletPurchase = () => {
		const pixelId = context.domain?.metaPixelEnabled ? context.domain?.metaPixelId : null;
		if (!pixelId) return;
		const pending = retrievePendingPurchase('wallet');
		trackMetaEvent({
			pixelId,
			eventName: 'Purchase',
			params: {
				content_type: 'wallet',
				content_ids: walletPackage ? [walletPackage._id] : undefined,
				content_name: walletPackage?.name || pending?.name,
				value: pending?.value || 0,
				currency: pending?.currency || context.domain?.metaCurrency || 'GBP'
			}
		});
	};

	const proceedToCheckout = async () => {
		trackWalletInitiateCheckout();
		setSubmitting(true);
		setError(null);
		try {
			const result = await Data.createWalletCheckout(id, basePath, showMarketingConsent && marketingConsent);
			if (isSumup) {
				setSumupCheckoutId(result.data.checkoutId || result.data.id || null);
				setSumupTransactionId(result.data.transactionId || null);
			} else {
				window.location.href = result.data.url;
			}
		} catch (error) {
			setError(error.response?.data?.message || 'Failed to start wallet checkout.');
			setSubmitting(false);
		}
	};

	useEffect(() => {
		if (!sumupCheckoutId || !sumupTransactionId) return;
		if (sumupPollRef.current) clearInterval(sumupPollRef.current);

		let cancelled = false;
		const poll = async () => {
			try {
				const result = await Data.verifyWalletSumupPayment(sumupTransactionId, sumupCheckoutId);
				if (!cancelled && result.status === 200) {
					fireWalletPurchase();
					navigate(`${basePath}?paymentStatus=success&transactionId=${sumupTransactionId}&checkoutId=${sumupCheckoutId}`);
				}
			} catch (error) {
				// Keep polling until the payment settles.
			}
		};

		poll();
		sumupPollRef.current = setInterval(poll, 3000);

		return () => {
			cancelled = true;
			if (sumupPollRef.current) {
				clearInterval(sumupPollRef.current);
				sumupPollRef.current = null;
			}
		};
	}, [sumupCheckoutId, sumupTransactionId, navigate, basePath]);

	if (loading) return <Loading button={false} />;
	if (!walletPackage) return null;

	return (
		<section className={styles.checkout}>
			<h2>Top Up Wallet</h2>

			<div className={styles.summary}>
				<h2>{walletPackage.name}</h2>
				<p>{formatMoney(walletPackage.purchaseAmount)}</p>
			</div>

			<div className={styles.intervalSelector}>
				<label>What you get</label>
				<p className={styles.subscriptionNote}>
					This package adds {formatBalance(walletPackage.creditAmount)} to your wallet balance.
				</p>
				{walletPackage.description ? <p className={styles.packageDescription}>{walletPackage.description}</p> : null}
			</div>

			{error ? <p className={styles.error}>{error}</p> : null}

			{showMarketingConsent ? (
				<label className={styles.marketingConsent}>
					<input
						type="checkbox"
						checked={marketingConsent}
						onChange={(e) => updateMarketingConsent(e.target.checked)}
					/>
					<span>
						<strong>Marketing consent</strong>
						<span>I agree to receive marketing and personalised offers.</span>
					</span>
				</label>
			) : null}

			{sumupCheckoutId ? (
				<div className={styles.sumupWrap}>
					<SumupWidget
						checkoutId={sumupCheckoutId}
						onSuccess={async () => {
							try {
								await Data.verifyWalletSumupPayment(sumupTransactionId, sumupCheckoutId);
								fireWalletPurchase();
								navigate(`${basePath}?paymentStatus=success&transactionId=${sumupTransactionId}&checkoutId=${sumupCheckoutId}`);
							} catch (error) {
								setError('There was an issue verifying your wallet payment. Please refresh.');
							}
						}}
						onError={(message) => setError(message || 'Payment failed. Please try again.')}
					/>
				</div>
			) : (
				<button type="button" onClick={proceedToCheckout} className="btn btnPrimary btn w-full mb-4" disabled={submitting}>
					{submitting ? 'Redirecting...' : `Pay ${formatMoney(walletPackage.purchaseAmount)}`}
				</button>
			)}

			<Link to={basePath} className="btn btnEmpty">Cancel</Link>
		</section>
	);
}

export default WalletCheckout;
